<template>
  <div class="app-tariff-details">
    <div class="card-wrapper">
      <div class="card card-tariff-details">
        <div v-show="isReady">
          <LoginConfirm ref="doLoginConfirmRef">
            <template #txtInvalidLoginConfirmPassword>
              <slot name="txtInvalidLoginConfirmPassword"></slot>
            </template>
          </LoginConfirm>
          <SimonModal
            :is-visible="showDismissalModal"
            @hide="showDismissalModal = false"
            id="dismiss-info-modal"
          >
            <slot v-if="modalType === 'dismissalCancelable'" name="txtDismissalModalCancelable"></slot>
            <slot v-else name="txtDismissalModal"></slot>
          </SimonModal>
          <slot name="txtBefore"></slot>
          <div class="tariff-details-headline">
            <slot name="txtTarifDetailsHeadline">Vertragsdaten</slot>
          </div>
          <table class="tariffs-details-wrapper selfservice" v-if="currentCustomer !== null">
            <tbody>
            <tr class="row tariff-detail-row" v-if="contract !== null && contract.msisdn">
              <td class="text tariff-detail-text">
                <slot name="lblNumber">Rufnummer</slot>
              </td>
              <td class="value tariff-detail-value">
                <msisdn :msisdn="contract.msisdn"></msisdn>
              </td>
            </tr>
            <tr class="row tariff-detail-row" v-if="currentCustomer.customerId">
              <td class="text tariff-detail-text">
                <slot name="lblCustomerNumber">Kundennummer</slot>
              </td>
              <td class="value tariff-detail-value">{{ currentCustomer.customerId }}</td>
            </tr>
            <tr class="row tariff-detail-row" v-show="bookedTariff !== null && bookedTariff.contractStartDate">
              <td class="text tariff-detail-text">
                <slot name="lblContractStart">Vertragsbeginn</slot>
              </td>
              <td class="value tariff-detail-value">{{ dateFormatter(new Date(bookedTariff.contractStartDate)) }}</td>
            </tr>
            <tr class="row tariff-detail-row" v-show="contract !== null && contract.state">
              <td class="text tariff-detail-text">
                <slot name="lblContractStatus">Vertragsstatus</slot>
              </td>
              <td class="value tariff-detail-value" v-if="this.contractPauseActive || this.contractPauseCreated">
                <div v-if="this.contractPauseCreated">
                  <slot name="txtPausedFrom">pausiert ab</slot>
                  {{ dateFormatter(new Date(this.contractPauseStartDate)) }}
                </div>
                <div v-else-if="this.contractPauseActive">
                  <slot name="txtPausedUntil">pausiert bis</slot>
                  {{ dateFormatter(new Date(this.contractPauseEndDate)) }}
                </div>
              </td>
              <td class="value tariff-detail-value contract-dismissal-pending"
                  v-else-if="hasPendingDismissal">
                <slot name="txtDismissalPending">gekündigt zum</slot>
                {{ formattedDismissalDate }}
              </td>
              <td class="value tariff-detail-value contract-active" v-else-if="contract.state === 'ACTIVE'">
                <slot name="txtActive">aktiv</slot>
              </td>
              <td class="value tariff-detail-value contract-dismissed" v-else>
                <slot name="txtDismissed">gekündigt</slot>
              </td>
            </tr>
            <tr class="row tariff-detail-row" v-show="contract !== null && contract.nextPossibleDismissalDate">
              <td class="text tariff-detail-text">
                <slot name="lblContractTerminable">Kündbar zum</slot>
                <a class="info-link-icon" @click="showDismissalModal=true ; modalType='terminable'"></a></td>
              <td class="value tariff-detail-value">{{ contract.nextPossibleDismissalDate }}</td>
            </tr>
            <tr class="row tariff-detail-row" v-show="hasPendingDismissal">
              <td class="text tariff-detail-text">
                <slot name="lblContractDismissalCancelable">Kündigung widerrufen spätestens bis zum</slot>
                <a class="info-link-icon" @click="showDismissalModal=true; modalType='dismissalCancelable'"></a></td>
              <td class="value tariff-detail-value">{{ formattedDismissalCancelableDate }}</td>
            </tr>
            </tbody>
          </table>
          <div v-if="showError" v-html="this.errorMsg" class="error-box"></div>
        </div>
      </div>
    </div>
    <div class="card-wrapper">
      <div class="card card-tariff-details">
        <table class="tariffs-details-wrapper selfservice downloads">
          <slot name="txtTarifDetailsDownloadsHeadline">Vertragsunterlagen</slot>
          <tbody>
          <tr class="row tariff-detail-row">
            <td class="text tariff-detail-text">
              <slot name="lblDownloadContractDocument">Dein Vertragsdokument</slot>
            </td>
            <td class="value tariff-detail-value">
              <button class="outline" @click.prevent.stop="checkContractDocument">
                <slot name="btnDownload">Herunterladen</slot>
              </button>
            </td>
          </tr>
          <tr class="row tariff-detail-row">
            <td class="text tariff-detail-text">
              <slot name="lblDownloadContractSummaryDocument">Vertragszusammenfassung</slot>
            </td>
            <td class="value tariff-detail-value">
              <button class="outline" @click.prevent.stop="checkContractSummaryDocument">
                <slot name="btnDownload">Herunterladen</slot>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
        <div v-if="showErrorDownload" v-html="this.errorMsg" class="error-box"></div>
      </div>

    </div>
  </div>
</template>

<script>
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import Msisdn from '@/components/generic/Msisdn.vue'
import { splitError } from '@/helper/splitError'
import customerContractState from '@/mixins/customerContractState'
import { mapActions, mapGetters } from 'vuex'
import LoginConfirm from '@/components/process/generic/Login/LoginConfirm.vue'
import doLoginConfirm from '@/mixins/doLoginConfirm'
import logout from '@/mixins/logout'
import axios from 'axios'
import FileSaver from 'file-saver'
import appFileSaver from '@/mixins/appFileSaver'
import dateFormatter from '@/mixins/formatters/dateFormatter'
import SimonModal from '@/components/modal/SimonModal.vue'

export default {
  name: 'TariffDetails',
  components: {
    SimonModal,
    Msisdn,
    LoginConfirm
  },
  data () {
    return {
      currentCustomer: null,
      tariffs: null,
      showDismissalModal: false,
      modalType: '',
      showError: false,
      showErrorDownload: false,
      errorMsg: {}
    }
  },
  mounted () {
    this.resetLoginConfirm()
  },
  mixins: [
    customerContractState(),
    doLoginConfirm('doLoginConfirmRef'),
    logout,
    appFileSaver,
    dateFormatter
  ],
  methods: {
    // Check Login and on success download document
    triggerDocumentDownload (document) {
      this.doLoginConfirm().then(() => {
        this.downloadDocument(document)
      }).catch((e) => {
        this.doLogout()
      })
      return false
    },
    downloadDocument (document) {
      const filename = document.identifier + '.pdf'

      axios.get(document.url, {
        baseURL: this.$config.apiUrl || '',
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          Accept: 'application/octet-stream'
        },
        responseType: 'blob'
      }).then((response) => {
        this.showErrorDownload = false
        const file = new Blob([response.data], { type: 'application/octet-stream' })
        if (this.appTriggerSaveAsFileAvailable()) {
          this.appTriggerSaveAsFile(filename, file)
        } else {
          FileSaver.saveAs(file, filename)
        }
        const data = {
          file_name: filename
        }
        if (typeof window.trackingNS?.track?.tracking === 'function') {
          window.trackingNS.track.tracking('download file', data)
        }
      }).catch((reason) => {
        const errorMsg = this.getSlotText('errorNoContractDocumentAvailable', 'Ufff! Leider kann dat Dokument gerade nicht geladen werden (Fehler: VD1). Bitte probier\'s später noch einmal.')
        this.errorMsg = errorMsg
        this.showErrorDownload = true
        const data = {
          type: 'technical',
          message: errorMsg.substring(0, 50),
          code: 'VD1'
        }
        if (typeof window.trackingNS?.track?.tracking === 'function') {
          window.trackingNS.track.tracking('error', data)
        }
      })
    },
    checkContractDocument () {
      const me = this
      const graphQlClient = me.$apolloProvider.defaultClient

      graphQlClient.query({
        query: gql`query consumerDocumentList ($filter: CustomerDocumentFilterInput!) {
          customerDocuments(customerDocumentFilter: $filter) {
            contracts { date identifier url }
          }
        }`,
        variables: {
          filter: {
            documentType: 'contract',
            from: '2021-06-22'
          }
        }
      }).then(response => {
        // Check response for contract documents - we are interested only in the first one
        const customerDocuments = response.data.customerDocuments
        if (!customerDocuments.contracts || !Array.isArray(customerDocuments.contracts) || customerDocuments.contracts.length === 0) {
          const errorMsg = this.getSlotText('errorNoContractDocumentAvailable', 'Ufff! Leider kann dat Dokument gerade nicht geladen werden (Fehler: VD1). Bitte probier\'s später noch einmal.')
          this.errorMsg = errorMsg
          this.showErrorDownload = true
          const data = {
            type: 'technical',
            message: errorMsg.substring(0, 50),
            code: 'VD1'
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('error', data)
          }
        } else {
          this.triggerDocumentDownload(customerDocuments.contracts[0])
          this.showErrorDownload = false
        }
      }).catch((error) => {
        splitError(error).forEach(e => {
          this.errorMsg = e.message
          me.$store.commit('addError', e)
          const data = {
            type: 'technical',
            message: e.message.substring(0, 50)
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('error', data)
          }
        })
        this.showErrorDownload = true
      })
    },
    checkContractSummaryDocument () {
      const contractSummary = {
        identifier: 'Vertragszusammenfassung',
        url: '/api/document/contract-summary'
      }
      this.triggerDocumentDownload(contractSummary)
    },
    ...mapActions('promotion/activePromotions', [
      'getActivePromotions'
    ])
  },
  computed: {
    contract () {
      return this.currentCustomer?.contract
    },
    bookedTariff () {
      return this.tariffs?.bookedTariff
    },
    isReady () {
      return !this.$apollo.queries.tariffs.loading && this.tariffs !== null && this.currentCustomer !== null && this.loadedContractState
    },
    ...mapGetters('promotion/activePromotions', [
      'getTest',
      'activePromotions',
      'creditIncentives',
      'volumeIncentives'
    ]),
    ...mapGetters([
      'accessToken'
    ])
  },
  apollo: {
    tariffs: function () {
      return wrapQuery(
        gql`query TariffDetails {
          currentCustomer {
            customerId
            contract {
              msisdn
              state
              nextPossibleDismissalDate
            }
          }
          tariffs {
            bookedTariff {
              contractStartDate
            }
          }
        }`, this, ['tariffs', 'currentCustomer'], {}, () => {
          this.showError = false
        },
        {},
        (error) => {
          splitError(error).forEach(e => {
            this.errorMsg = e.message
          })
          if (!this.showError) {
            const data = {
              type: 'technical',
              message: this.errorMsg
            }
            if (typeof window.trackingNS?.track?.tracking === 'function') {
              window.trackingNS.track.tracking('error', data)
            }
          }
          this.showError = true
        }
      )
    }
  }
}
</script>
